import React from 'react'
import { PieChart, Pie, Tooltip, Cell } from 'recharts'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import BodyWidget from '@SonarCX/components/atoms/BodyWidget'
import LoadingSpinner from '@SonarCX/components/atoms/LoadingSpinner'

import { getTypeName, getTypeColor } from './helpers'
import { selectTaskType, fetchTypes } from '@SonarCX/slices/taskType'
import { useStats } from '@SonarCX/providers/StatsProvider'
import useStaticChartPosition from '../hooks/useStaticChartPosition'
import useStyles from './styles'

type TaskTypesProps = {
  mineOnly?: boolean
}

const TaskTypes: React.FC<TaskTypesProps> = ({ mineOnly }) => {
  const dispatch = useAppDispatch()

  const {
    typesValues: taskTypes,
    loaded: typesLoaded,
    loading: typesLoading
  } = useAppSelector(selectTaskType)

  const { taskStats, taskLoaded, globalTasks, globalLoaded } = useStats()
  const { containerRef, chartRef } = useStaticChartPosition()

  const loaded = React.useMemo(
    () => (mineOnly ? taskLoaded : globalLoaded),
    [mineOnly, taskLoaded, globalLoaded]
  )

  const stats = React.useMemo(
    () => (mineOnly ? taskStats : globalTasks),
    [mineOnly, taskStats, globalTasks]
  )

  const formatted = React.useMemo(() => {
    if (!stats) {
      return []
    }

    const newStats = [...stats.types]

    if (!newStats.length) {
      return []
    }

    return newStats.map(stat => {
      const { id, value } = stat
      const name = getTypeName(taskTypes, id)
      const color = getTypeColor(id)

      return {
        id,
        value,
        name,
        color
      }
    })
  }, [stats, taskTypes])

  const empty = React.useMemo(() => (formatted.length ?? 0) === 0, [formatted])

  const classes = useStyles()

  React.useEffect(() => {
    if (!typesLoaded && !typesLoading) {
      dispatch(fetchTypes())
    }
  }, [typesLoaded, typesLoading, dispatch])

  return (
    <BodyWidget
      ref={containerRef}
      title={`${mineOnly ? 'My' : 'All'} Tasks by Type`}
    >
      <div className={classes.statsContainer}>
        {!loaded && (
          <div style={{ position: 'relative', flex: '1' }}>
            <LoadingSpinner />
          </div>
        )}

        {loaded && empty && <div className={classes.emptyTitle}>No tasks</div>}

        {loaded && !empty && (
          <>
            <div className={classes.header}>
              <div>
                {formatted.map(type => (
                  <div key={type.id} className={classes.typesInfo}>
                    <div
                      className={classes.dot}
                      style={{
                        marginRight: '1rem',
                        backgroundColor: type.color
                      }}
                    />
                    {type.name}: {type.value}
                  </div>
                ))}
              </div>
            </div>

            <div className={classes.chartContainer}>
              <div ref={chartRef} className={classes.pieChart}>
                <PieChart width={207} height={200}>
                  <Pie
                    data={formatted}
                    innerRadius={35}
                    outerRadius={75}
                    fill='#8884d8'
                    paddingAngle={5}
                    dataKey='value'
                  >
                    {formatted.map(type => (
                      <Cell key={`cell-${type.id}`} fill={type.color} />
                    ))}
                  </Pie>

                  <Tooltip wrapperStyle={{ outline: 'none' }} />
                </PieChart>
              </div>
            </div>
          </>
        )}
      </div>
    </BodyWidget>
  )
}

export default TaskTypes
